import React, { useEffect, useState } from "react";
import { ContentMainLayout } from "../../layout/contentLayout";
import { Box, Button, Stack, Typography } from "@mui/material";
import useResponsive from "../../hooks/useResponsive";
import ReportSummaryContainer from "../report-summary-container";
import BadgeImage from "../ui-elements/badge-image";
import ImageGrid from "../image-grid";
import CommentContainer from "../ui-elements/comment-container";
import TypographyContent from "../ui-elements/typography-content";
import { PostContentDetails } from "../../interface/posts";
import {
  formatDateAndTimeFromString,
  formatTextType,
} from "../../utils/date-setup";
import { CommentReplyPropsData } from "../../interface/comment";
import TurnedInIcon from "@mui/icons-material/TurnedIn";
import TurnedInNotIcon from "@mui/icons-material/TurnedInNot";
import { apiBookmarkPost } from "../../api/post";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { FlagModalContents } from "../ui-elements/flag-post";
import { FlagOutlineIcon, FlagContainedIcon } from "../ui-elements/svg-icons";
import { useDrawer } from "../../hooks/useDrawer";

import Comment from "../ui-elements/comment-content";
import { useCommentAndReply } from "../../hooks/usecomment";
import {
  apiAddComment,
  apiAddReply,
  apiGetCommentReply,
  apiLikeCommentReply,
} from "../../utils/comment-utils";

const ReportContent = ({
  post_details,
  comment_data,
  refetch_comment,
}: {
  post_details: PostContentDetails;
  comment_data: any[];
  refetch_comment: () => void;
}) => {
  const isDesktop = useResponsive("down", "lg", "xl");
  const isMedium = useResponsive("down", "md", "xl");
  const postId = post_details?.id;
  const initialComments = comment_data;

  const account_profile_data = useSelector(
    (state: RootState) => state.content.account_profile_data
  );

  const { openDrawer } = useDrawer();
  const {
    comments,
    newCommentContent,
    setNewCommentContent,
    handleAddComment,
    handleAddReply,
    handleLike,
    handleLoadReplies,
    loadingStates,
    expandedComments,
  } = useCommentAndReply({
    postId,
    initialComments,
    onAddComment: async (content, postId) => {
      return apiAddComment(content, postId, account_profile_data);
    },
    onAddReply: async (parentId, content, postId) => {
      return apiAddReply(parentId, content, postId, account_profile_data);
    },
    onLike: async (commentId) => {
      // Mock or actual API call here
      return apiLikeCommentReply(commentId);
    },
    onLoadReplies: async (commentId) => {
      // Mock or actual API call here
      return apiGetCommentReply(commentId, postId);
    },
  });

  return (
    <ContentMainLayout>
      <Stack direction={"row"} spacing={4} justifyContent={"space-between"}>
        <Stack width={isDesktop ? "90%" : "65%"}>
          <Box
            id="main-report-scroll-container"
            sx={{
              width: isMedium ? "100%" : "95%",
              mx: 2,
              mt: "auto",
              height: "76vh",
              overflowY: "auto",
              pt: "85px",
              px: isMedium ? 1 : 3,
              position: "relative",
            }}
          >
            {isDesktop && (
              <Box
                sx={{ position: "fixed", right: 10, width: 80, height: "3rem" }}
              >
                <Button
                  variant="contained"
                  onClick={() =>
                    openDrawer(
                      <ReportSummaryContainer post_id={post_details?.id} />
                    )
                  }
                >
                  IRep
                </Button>
              </Box>
            )}
            <ReportContentDetails
              post_details={post_details}
              comment_list={comments}
              handleLoadReplies={handleLoadReplies}
              handleAddReply={handleAddReply}
              handleLike={handleLike}
              expandedComments={expandedComments}
              loadingStates={loadingStates}
            />

            <Box
              sx={{
                position: "fixed",
                width: isMedium ? "80%" : 600,
                mx: "auto",
                bottom: 10,
                p: 1,
                py: 2,
                backgroundColor: "#FAFAFA",
              }}
            >
              <CommentContainer
                newCommentContent={newCommentContent}
                setNewCommentContent={setNewCommentContent}
                handleAddComment={handleAddComment}
                data={post_details}
              />
            </Box>
          </Box>
        </Stack>
        <Stack
          width={isDesktop ? "0" : "35%"}
          height={505}
          sx={{ display: isDesktop ? "none" : "block" }}
        >
          <Box
            sx={{
              width: "100%",
              pt: "85px",
              height: "76vh",
            }}
          >
            <ReportSummaryContainer post_id={post_details?.id} />
          </Box>
        </Stack>
      </Stack>
    </ContentMainLayout>
  );
};

export default ReportContent;

export const ReportContentDetails = ({
  post_details,
  comment_list,
  handleLoadReplies,
  handleAddReply,
  handleLike,
  expandedComments,
  loadingStates,
}: {
  post_details: PostContentDetails;
  comment_list: any[];
  handleLoadReplies: (commentId: number) => Promise<void>;
  handleAddReply: (parentId: number, content: string) => Promise<void>;
  loadingStates: {
    [key: number]: boolean;
  };
  expandedComments: Set<number>;
  handleLike: (commentId: number) => Promise<void>;
}) => {
  const [bookmarked, setBookmarked] = useState(
    post_details?.current_user_bookmarked
  );
  const [flag_modal, setFlag_modal] = useState(false);
  const [flag, setFlag] = useState(false);

  const toggle_modal = () => setFlag_modal(!flag_modal);
  const toggle_flag = () => setFlag(!flag);

  // handle bookmark
  const bookmark_post = async (post_id: number) => {
    try {
      const response = await apiBookmarkPost(post_id);
      if (response.status === 200) {
        //
      } else {
      }
    } catch (error) {}
  };

  const handleBookmark = (id: number) => {
    if (bookmarked) {
      setBookmarked(false);
      bookmark_post(id);
    } else {
      setBookmarked(true);
      bookmark_post(id);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <FlagModalContents
        open={flag_modal}
        toggle_flag={toggle_flag}
        handle_modal={toggle_modal}
        post_id={post_details?.id}
        reported={post_details?.reported}
      />
      <Box sx={{ pb: "3rem" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={1} alignItems={"center"}>
            <img
              src={post_details?.author_photo_url}
              style={{ width: 50, height: 50, borderRadius: 40 }}
            />
            <Stack>
              <Stack direction={"row"} spacing={1}>
                <Typography variant="subtitle2" color="black">
                  {post_details?.author}
                </Typography>
                <BadgeImage badge={post_details?.author_badge} />
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  color: "#4A5E6D",
                  fontWeight: 400,
                }}
              >
                {formatDateAndTimeFromString(post_details?.created_at)}
              </Typography>
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  fontWeight: 400,
                  fontStyle: "italic",
                }}
              >
                {formatTextType(post_details?.post_type)}
              </Typography>
            </Stack>
          </Stack>
          <Stack direction={"row"} spacing={1}>
            <Button onClick={toggle_modal}>
              {flag ? <FlagContainedIcon /> : <FlagOutlineIcon />}
            </Button>
            <Button onClick={() => handleBookmark(post_details?.id)}>
              {bookmarked ? <TurnedInIcon /> : <TurnedInNotIcon />}
            </Button>
          </Stack>
        </Box>
        <Box sx={{ ml: 4 }}>
          <Stack>
            <Typography
              color="black"
              sx={{
                fontSize: 18,
                marginBottom: 2,
                fontWeight: 500,
              }}
            >
              {post_details?.title}
            </Typography>
            <ImageGrid
              images={post_details?.media} // Pass images as an array
            />
          </Stack>
          <Box sx={{ mt: "10px", display: "block" }}>
            <TypographyContent content={post_details?.context} />
          </Box>
        </Box>
        {/* {comment_list?.length > 0 &&
          comment_list?.map((comment, index) => (
            <CommentUI
              key={index}
              id={comment?.id}
              author_img={comment?.author_photo_url}
              author_name={comment?.author_name}
              date={comment?.commented_at}
              comment={comment?.comment}
              likes={comment?.likes}
              sx={{}}
              handle_open_reply={handle_open_reply}
              replies={comment?.replies} // Optional since replies may be undefined
            />
          ))} */}
        {comment_list?.length > 0 && (
          <Typography
            variant="h6"
            color="primary"
            sx={{ fontWeight: 600, mt: 4 }}
          >
            Comments
          </Typography>
        )}
        {comment_list.map((comment) => (
          <Comment
            key={comment.id}
            comment={comment}
            onLoadReplies={handleLoadReplies}
            onAddReply={handleAddReply}
            onLike={handleLike}
            isExpanded={expandedComments.has(comment.id)}
            isLoading={loadingStates[comment.id] || false}
            level={0}
          />
        ))}
      </Box>
    </Box>
  );
};
